import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { LinkedinTypes } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LinkedinAccount, LinkedinAccountStatuses } from '../linkedin-accounts/linkedin-accounts.types';
import { getTaskInitials, TmSectionCard, TmTaskRow } from './tm-section-card';

interface ItemProps {
  task: LinkedinAccount;
  syncLoading: boolean;

  handleOpenAppSyncAndSynchronizeLiAccount(): void;

  handleOpenAppSyncAndCorrectAccessData(): void;

  handleOpenAppSyncAndVerifyPin(): void;

  handleOpenAppSyncAndSolveCaptcha(): void;

  handleOpenAppSyncThirdPartyWebsite(): void;

  handleOpenRecruiterParallelLoginDialog(): void;

  handleOpenOnboardingIncompleteDialog(): void;

  handleOpenFairUsePauseDialog(): void;

  handleOpenTwoFaNotEnabledDialog(): void;
}

const TaskItem: React.FC<ItemProps> = ({
  task,
  syncLoading,
  handleOpenAppSyncAndSynchronizeLiAccount,
  handleOpenAppSyncAndCorrectAccessData,
  handleOpenAppSyncAndVerifyPin,
  handleOpenAppSyncAndSolveCaptcha,
  handleOpenAppSyncThirdPartyWebsite,
  handleOpenRecruiterParallelLoginDialog,
  handleOpenOnboardingIncompleteDialog,
  handleOpenFairUsePauseDialog,
  handleOpenTwoFaNotEnabledDialog,
}) => {
  const { t } = useTranslation();

  const loading = syncLoading;

  const { userEmail, firstName, lastName, liProductType, status } = task;

  const userName = `${firstName} ${lastName}`.trim() || '-';

  const handleAction = !loading
    ? status === LinkedinAccountStatuses.SYNCHRONIZED
      ? undefined
      : status === LinkedinAccountStatuses.NOT_SYNCHRONIZED
        ? handleOpenAppSyncAndSynchronizeLiAccount
        : status === LinkedinAccountStatuses.TWO_FA
          ? handleOpenAppSyncAndVerifyPin
          : status === LinkedinAccountStatuses.WRONG_CREDENTIALS
            ? handleOpenAppSyncAndCorrectAccessData
            : status === LinkedinAccountStatuses.TIMED_OUT_CAPTCHA
              ? handleOpenAppSyncAndSynchronizeLiAccount
              : status === LinkedinAccountStatuses.WRONG_PIN
                ? handleOpenAppSyncAndSynchronizeLiAccount
                : status === LinkedinAccountStatuses.CAPTCHA
                  ? handleOpenAppSyncAndSolveCaptcha
                  : status === LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN
                    ? handleOpenRecruiterParallelLoginDialog
                    : status === LinkedinAccountStatuses.CHOOSE_CONTRACT
                      ? handleOpenAppSyncAndSynchronizeLiAccount
                      : status === LinkedinAccountStatuses.ONBOARDING_INCOMPLETE
                        ? handleOpenOnboardingIncompleteDialog
                        : status === LinkedinAccountStatuses.FAIR_USE_PAUSE
                          ? handleOpenFairUsePauseDialog
                          : status === LinkedinAccountStatuses.THIRD_PARTY || status === LinkedinAccountStatuses.THIRD_PARTY_TEXT
                            ? handleOpenAppSyncThirdPartyWebsite
                            : status === LinkedinAccountStatuses.TWO_FA_NOT_ENABLED
                              ? handleOpenTwoFaNotEnabledDialog
                              : undefined
    : undefined;

  const statusText =
    status === LinkedinAccountStatuses.SYNCHRONIZED
      ? t('linkedinAccountLoggedIn')
      : status === LinkedinAccountStatuses.NOT_SYNCHRONIZED
        ? t('linkedinAccountLoggedOut')
        : status === LinkedinAccountStatuses.TWO_FA
          ? t('enter2fa')
          : status === LinkedinAccountStatuses.WRONG_CREDENTIALS
            ? t('correctAccessData')
            : status === LinkedinAccountStatuses.TIMED_OUT_CAPTCHA
              ? t('requestNewCaptcha')
              : status === LinkedinAccountStatuses.WRONG_PIN
                ? t('requestNewCode')
                : status === LinkedinAccountStatuses.CAPTCHA
                  ? t('solveCaptcha')
                  : status === LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN
                    ? t('parallelLogin')
                    : status === LinkedinAccountStatuses.CHOOSE_CONTRACT
                      ? t('chooseContract')
                      : status === LinkedinAccountStatuses.ONBOARDING_INCOMPLETE
                        ? t('incompleteOnboarding')
                        : status === LinkedinAccountStatuses.FAIR_USE_PAUSE
                          ? t('fairUsePause')
                          : status === LinkedinAccountStatuses.THIRD_PARTY || status === LinkedinAccountStatuses.THIRD_PARTY_TEXT
                            ? t('thirdPartyLogin')
                            : status === LinkedinAccountStatuses.TWO_FA_NOT_ENABLED
                              ? t('twoFaNotEnabled')
                              : status;

  const productLabel =
    liProductType === LinkedinTypes.LINKEDIN
      ? 'LinkedIn'
      : liProductType === LinkedinTypes.SALES_NAV
        ? 'Sales Navigator'
        : liProductType === LinkedinTypes.RECRUITER
          ? 'Recruiter'
          : '';

  return (
    <TmTaskRow
      initials={getTaskInitials(firstName, lastName, userEmail)}
      name={userName || t('linkedinUser')}
      meta={_.compact([userEmail, productLabel]).join(' · ')}
      context={statusText}
      contextLabel={t('accountStatusLabel')}
      actionText={t('connectAccountTask')}
      disabled={_.isNil(handleAction)}
      handleAction={handleAction ?? _.noop}
    />
  );
};

interface Props {
  tasks: LinkedinAccount[];
  total: number | undefined;
  syncLoading: boolean;

  handleOpenAppSyncAndSynchronizeLiAccount(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndCorrectAccessData(account: LinkedinAccount): () => void;

  handleOpenRecruiterParallelLoginDialog(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndVerifyPin(account: LinkedinAccount): () => void;

  handleOpenAppSyncAndSolveCaptcha(account: LinkedinAccount): () => void;

  handleOpenAppSyncThirdPartyWebsite(account: LinkedinAccount): () => void;

  handleOpenOnboardingIncompleteDialog(account: LinkedinAccount): () => void;

  handleOpenFairUsePauseDialog(): void;

  handleOpenTwoFaNotEnabledDialog(account: LinkedinAccount): () => void;
}

const TmLinkedinAccounts: React.FC<Props> = ({
  tasks,
  total,
  syncLoading,
  handleOpenAppSyncAndSynchronizeLiAccount,
  handleOpenAppSyncAndCorrectAccessData,
  handleOpenRecruiterParallelLoginDialog,
  handleOpenAppSyncAndVerifyPin,
  handleOpenAppSyncAndSolveCaptcha,
  handleOpenAppSyncThirdPartyWebsite,
  handleOpenOnboardingIncompleteDialog,
  handleOpenFairUsePauseDialog,
  handleOpenTwoFaNotEnabledDialog,
}) => {
  const { t } = useTranslation();

  return (
    <TmSectionCard
      icon={<PriorityHighIcon fontSize={'small'} />}
      iconBackground={'#f2caca'}
      iconColor={'#930202'}
      title={t('accountsToFix')}
      count={total}
      hint={t('accountsToFixHint')}
    >
      {tasks.map((account) => (
        <TaskItem
          key={account._id}
          task={account}
          syncLoading={syncLoading}
          handleOpenAppSyncAndSynchronizeLiAccount={handleOpenAppSyncAndSynchronizeLiAccount(account)}
          handleOpenAppSyncAndCorrectAccessData={handleOpenAppSyncAndCorrectAccessData(account)}
          handleOpenAppSyncAndVerifyPin={handleOpenAppSyncAndVerifyPin(account)}
          handleOpenAppSyncAndSolveCaptcha={handleOpenAppSyncAndSolveCaptcha(account)}
          handleOpenAppSyncThirdPartyWebsite={handleOpenAppSyncThirdPartyWebsite(account)}
          handleOpenRecruiterParallelLoginDialog={handleOpenRecruiterParallelLoginDialog(account)}
          handleOpenOnboardingIncompleteDialog={handleOpenOnboardingIncompleteDialog(account)}
          handleOpenFairUsePauseDialog={handleOpenFairUsePauseDialog}
          handleOpenTwoFaNotEnabledDialog={handleOpenTwoFaNotEnabledDialog(account)}
        />
      ))}
    </TmSectionCard>
  );
};

export default TmLinkedinAccounts;
